import { produce } from 'immer';
import { Action, Reducer } from 'redux';
import { GlobalState } from '.';
import { Message } from '../models/Message';

export enum ActionTypes {
    ShowMessage = '@@message/SHOW_MESSAGE',
    DismissMessage = '@@message/DISMISS_MESSAGE'
}

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface MessageState {
    messages: Message[];
}

export const initialState: MessageState = {
    messages: []
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
// Use @typeName and isActionType for type detection that works even after serialization/deserialization.

export interface ShowMessage { type: ActionTypes.ShowMessage, message: Message }
export interface DismissMessage { type: ActionTypes.DismissMessage, message: Message }

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type Actions = 
    | ShowMessage
    | DismissMessage;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
    show: (message: Message) => ({ type: ActionTypes.ShowMessage, message } as ShowMessage),
    dismiss: (message: Message) => ({ type: ActionTypes.DismissMessage, message } as DismissMessage)
};

// ----------------
// SELECTORS - These are functions that, given the application state, will access a particular piece of information
// from the current store

export const selectors = {
    currentMessage: (state: GlobalState) => (state.message.messages.length || 0) > 0 ? state.message.messages[0] : undefined
}

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducer: Reducer<MessageState> = (state: MessageState | undefined, incomingAction: Action): MessageState => {
    if (state === undefined) {
        return initialState;
    }

    const action = incomingAction as Actions;
    switch (action.type) {
        case ActionTypes.ShowMessage: {
            const newState = produce(state, draft => {
                draft.messages.push(action.message);
            });
            return newState;
        }
        case ActionTypes.DismissMessage: {
            const newState = produce(state, draft => {
                draft.messages = state.messages.filter(m => m.id !== action.message.id)
            });
            return newState
        }
        default: {
            return state;
        }
    }
};
